import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import accountApi from "@/lib/accountApiClient";
import {
  clearAccountReturnIntent,
  clearAccountToken,
  getAccountToken,
  removePendingClaim,
  setAccountToken,
} from "@/lib/accountStorage";

const AccountContext = createContext(null);

export function AccountProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savedResultCodes, setSavedResultCodes] = useState(() => new Set());

  const clearAccountSession = useCallback(() => {
    clearAccountToken();
    setUser(null);
    setSavedResultCodes(new Set());
  }, []);

  const refreshMe = useCallback(async () => {
    const token = getAccountToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return null;
    }

    setLoading(true);
    try {
      const { data } = await accountApi.get("/account/auth/me");
      setUser(data);
      return data;
    } catch (error) {
      if ([401, 403].includes(error?.response?.status)) clearAccountSession();
      else setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [clearAccountSession]);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  const authenticate = useCallback(async (path, email, password, extra = {}) => {
    const { data } = await accountApi.post(path, { email: String(email || "").trim(), password, ...extra });
    if (!data?.token || !data?.user) throw new Error("Invalid account response");
    if (!setAccountToken(data.token)) throw new Error("Account session storage unavailable");
    setUser(data.user);
    setSavedResultCodes(new Set());
    return data.user;
  }, []);

  const login = useCallback(
    (email, password) => authenticate("/account/auth/login", email, password),
    [authenticate],
  );
  const register = useCallback(
    (email, password, profile) => authenticate(
      "/account/auth/register",
      email,
      password,
      profile ? { profile } : {},
    ),
    [authenticate],
  );

  const updateProfile = useCallback(async (profile) => {
    try {
      const { data } = await accountApi.put("/account/profile", profile);
      setUser(data);
      return data;
    } catch (error) {
      if ([401, 403].includes(error?.response?.status)) clearAccountSession();
      throw error;
    }
  }, [clearAccountSession]);

  const logout = useCallback(async () => {
    try {
      if (getAccountToken()) await accountApi.post("/account/auth/logout");
    } catch {
      // Phase 1 logout is stateless; local cleanup is authoritative for this tab.
    } finally {
      clearAccountSession();
    }
  }, [clearAccountSession]);

  const claimResult = useCallback(async (anonymousCode, claimToken) => {
    try {
      const { data } = await accountApi.post("/account/claim-result", {
        anonymous_code: anonymousCode,
        claim_token: claimToken,
      });
      removePendingClaim(anonymousCode);
      setSavedResultCodes((current) => new Set(current).add(anonymousCode));
      return data;
    } catch (error) {
      if ([401, 403].includes(error?.response?.status)) clearAccountSession();
      throw error;
    }
  }, [clearAccountSession]);

  const requestEmailVerification = useCallback(async (lang) => {
    const { data } = await accountApi.post("/account/email-verification/request", { lang });
    return data;
  }, []);

  const deleteAccount = useCallback(async (password) => {
    try {
      const { data } = await accountApi.delete("/account", {
        data: { password, confirmation: true },
      });
      clearAccountReturnIntent();
      clearAccountSession();
      return data;
    } catch (error) {
      if ([401, 403].includes(error?.response?.status)) clearAccountSession();
      throw error;
    }
  }, [clearAccountSession]);

  const isResultSaved = useCallback(
    (anonymousCode) => savedResultCodes.has(anonymousCode),
    [savedResultCodes],
  );

  const value = useMemo(() => ({
    user,
    loading,
    isAuthenticated: Boolean(user),
    login,
    register,
    logout,
    refreshMe,
    clearAccountSession,
    claimResult,
    isResultSaved,
    requestEmailVerification,
    deleteAccount,
    updateProfile,
  }), [
    user,
    loading,
    login,
    register,
    logout,
    refreshMe,
    clearAccountSession,
    claimResult,
    isResultSaved,
    requestEmailVerification,
    deleteAccount,
    updateProfile,
  ]);

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
}

export function useAccount() {
  const context = useContext(AccountContext);
  if (!context) throw new Error("useAccount outside AccountProvider");
  return context;
}
